import { useEffect } from 'react';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { blockCard } from 'api/cards/cards';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { BlockCardBody } from 'api/cards/cards.types';
import { useSelectedCard } from 'hooks/useSelectedCard/useSelectedCard';
import { useMutation } from 'hooks/useMutation/useMutation';
import { CardBlockTypeEnum } from 'api/cards/cards.enum';
import { AppRouteWithParams } from 'routing/AppRoute.enum';
import { useIsExactPath } from 'hooks/useIsExactPath/useIsExactPath';
import { useModalState } from 'hooks/useModalState/useModalState';

import { BlockCard } from './BlockCard';
import { BlockCardContainerProps } from './BlockCard.types';
import { useBlockCardValidation } from './BlockCard.validation';

export const BlockCardContainer = ({ selectedCard }: BlockCardContainerProps) => {
  const queryClient = useQueryClient();
  const isHistoryView = useIsExactPath(AppRouteWithParams.cardHistory.path);

  const { formatMessage } = useLocale();
  const { setSelectedCard } = useSelectedCard();

  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const { mutate: onBlockCard, isLoading } = useMutation(blockCard, {
    onSuccess: (_, { body }) => {
      queryClient.invalidateQueries([isHistoryView ? QueryKeyEnum.CARD_HISTORY : QueryKeyEnum.CARDS]);
      setSelectedCard({ ...selectedCard, ...body, blokada: CardBlockTypeEnum.RECZNA });
      toast.dark(formatMessage({ id: AppMessages['card.block.success'] }));

      onModalClose();
    },
  });

  const { schema } = useBlockCardValidation();
  const formMethods = useForm<BlockCardBody>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const onSubmit = (body: BlockCardBody) => {
    onBlockCard({ id: selectedCard.id, body });
  };

  useEffect(() => {
    formMethods.reset({ powodBlokady: '' });
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <BlockCard
        selectedCard={selectedCard}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
